import type { ComputedRef } from 'vue-demi';
import { computed } from 'vue-demi';
import type { MaybeRefOrGetter } from '@vueuse/shared';
import { jwtDecode } from 'jwt-decode';
import type { JwtDecodeOptions, JwtHeader, JwtPayload } from 'jwt-decode';

export interface UseJwtOptions<Fallback> {
  /**
   * Value returned when decoding fails.
   *
   * @default null
   */
  fallbackValue?: Fallback;

  /**
   * Optional error callback triggered when decoding throws.
   */
  onError?: (error: unknown) => void;
}

/**
 * Return type for `useJwt`.
 */
export interface UseJwtReturn<Payload, Header, Fallback> {
  /**
   * The decoded JWT header as a computed ref.
   */
  header: ComputedRef<Header | Fallback>;

  /**
   * The decoded JWT payload as a computed ref.
   */
  payload: ComputedRef<Payload | Fallback>;
}

/**
 * Provides a reactive decoded JWT header and payload from an encoded JWT string.
 *
 * @see https://vueuse.org/useJwt
 *
 * @param encodedJwt - A string or reactive getter of the encoded JWT.
 * @param options - Optional fallback and error handling behavior.
 *
 * @returns A reactive object with `header` and `payload` computed refs.
 *
 * @example
 * const token = ref(localStorage.getItem('token') || '');
 * const { payload, header } = useJwt(token);
 *
 * watch(payload, () => {
 *   if (payload.value?.exp && Date.now() / 1000 > payload.value.exp) {
 *     console.warn('Token expired');
 *   }
 * });
 */
export function useJwt<
  Payload extends object = JwtPayload,
  Header extends object = JwtHeader,
  Fallback = null,
>(
  encodedJwt: MaybeRefOrGetter<string>,
  options: UseJwtOptions<Fallback> = {},
): UseJwtReturn<Payload, Header, Fallback> {
  const { onError, fallbackValue = null } = options;

  const decodeWithFallback = <T extends object>(
    encodedJwt: string,
    options?: JwtDecodeOptions,
  ): T | Fallback => {
    try {
      return jwtDecode<T>(encodedJwt, options);
    }
    catch (err) {
      onError?.(err);
      return fallbackValue as Fallback;
    }
  };

  const header = computed(() =>
    decodeWithFallback<Header>(toValue(encodedJwt), { header: true }),
  );
  const payload = computed(() =>
    decodeWithFallback<Payload>(toValue(encodedJwt)),
  );

  return {
    header,
    payload,
  };
}
